import { AfterViewInit, Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngneat/elf';
import { SelectItem } from 'primeng/api';

@Component({
  selector: 'app-thermal-results-plot',
  template: `
    <app-xy-line-plot
      [plotData]="plot.data"
      [xAxisTitle]="xAxisTitle"
      [yAxisTitle]="yAxisTitle"
      [plotName]="plotName"
      [downloadPlotName]="downloadPlotName"
      [plotTitle]="plotTitle"
      [legendGroupClick]="'toggle'"
      [xyLinePlotStore]="thermalResultsStore"
      [xAxisRange]="xAxisRange"
    ></app-xy-line-plot>
  `,
  styles: [':host { display: flex; flex: 1 1 0%; min-height: 0; overflow: hidden; }'],
  standalone: false
})
export class ThermalResultsPlotComponent implements OnChanges, AfterViewInit {

  public depthDisplay: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public missingResults = false;
  public yAxisTitle = '';

  @Input() public xAxisTitle = '';
  @Input() public depthUnit = '';
  @Input() public temperatureUnit = '';
  @Input() private diameterUnit = '';
  @Input() private plotData: any[];
  @Input() private columnDefinitions: any[];
  @Input() public plotTitle = '';
  @Input() selectedDepthDisplay = 'md';
  @Input() isAnnularResults = false;
  @Input() xAxisTickFormatNumber = '';
  @Input() plotName = '';
  @Input() tubulars: any[] = [];
  @Input() selectedAnnulus = '';
  @Input() downloadPlotName = '';
  @Input() thermalResultsStore: Store;
  @Input() xAxisRange: number[] = null;

  public plot = {
    data: [],
    layout: {
      xaxis: {
        autorange: true
      },
      yaxis: {
        title: {
          text: ''
        },
        autorange: 'reversed'
      }
    },
    config: {
      scrollZoom: false
    }
  };

  ngOnChanges(): void {
    this.parseResultsForPlot();
    this.resetAxes();
  }

  private resetAxes(): void {
    this.plot.layout.xaxis.autorange = true;
    this.plot.layout.yaxis.autorange = 'reversed';
  }

  ngAfterViewInit() {
    if (this.plot.data.length === 0) {
      this.missingResults = true
    } else {
      this.missingResults = false;
    }
  }

  parseResultsForPlot() {
    if (this.plotData === undefined) { return; }
    this.plot.config.scrollZoom = this.plotData ? true : false;
    const traceArray = [];

    const wellCylinders: { topMd: number; shoeMd: number; tubularStrings: any[] }[] = [];
    for (const tubular of this.tubulars || []) {
      const isTubing = tubular.type === 'Tubing';
      const linerCylinder = isTubing
        ? (wellCylinders[wellCylinders.length - 1] ?? null)
        : (wellCylinders.find(x => x.topMd >= tubular.shoeMd) ?? null);

      if (linerCylinder == null || (isTubing && linerCylinder.topMd < tubular.shoeMd)) {
        wellCylinders.push({ topMd: tubular.hangerMd, shoeMd: tubular.shoeMd, tubularStrings: [tubular] });
      } else {
        const combined = [tubular, ...linerCylinder.tubularStrings];
        const cylinderIndex = wellCylinders.indexOf(linerCylinder);
        wellCylinders[cylinderIndex] = { topMd: tubular.hangerMd, shoeMd: linerCylinder.shoeMd, tubularStrings: combined };
      }
    }

    this.columnDefinitions?.forEach((element => {
      if (element.field === 'md' || element.field === 'tvd') {
        return;
      } else {
        if (element.header != `MD (${this.depthUnit})` && element.header != `TVD (${this.depthUnit})`) {
          const stringIndex = this.tubulars.findIndex(x => this.selectedAnnulus?.startsWith(x.name.substring(0, x.name.indexOf(this.diameterUnit))) ?? element.header.startsWith(x.name.substring(0, x.name.indexOf(this.diameterUnit))));
          let shoe = 0;
          if (stringIndex >= 0) {
            const currentTubular = this.tubulars[stringIndex];
            const cylinder = wellCylinders.find(c => c.tubularStrings.some(t => t.name === currentTubular.name));
            shoe = cylinder?.shoeMd ?? currentTubular.shoeMd;
          }

          let shoeIndex = this.plotData.findIndex(x => x[0] >= shoe);
          if (shoeIndex <= 0 || element.header.includes('Drill')) {
            shoeIndex = this.plotData.length - 1;
          }
          if (element.header?.endsWith(` ${this.depthUnit} (${this.temperatureUnit})`) || this.selectedAnnulus?.endsWith(` ${this.depthUnit} (${this.temperatureUnit})`)) {
            shoeIndex = 0;
          }
          if (!this.selectedAnnulus?.endsWith(`(${this.temperatureUnit})`)) {
            traceArray.push({
              name: element.header,
              x: this.plotData.map(r => r[this.columnDefinitions.indexOf(element)]).slice(0, shoeIndex + 1),
              y: this.selectedDepthDisplay === 'md' ? this.plotData.map(r => r[0]) : this.plotData.map(r => r[1]).slice(0, shoeIndex + 1),
              mode: 'lines',
              line: {
                dash: 'solid',
                width: 1.25
              },
              legendgroup: element.header
            });

            if (this.plotName?.startsWith('thermalTemperatureResults') && shoeIndex < this.plotData.length - 1) {
              traceArray.push({
                name: element.header.endsWith(` ${this.depthUnit} (${this.temperatureUnit})`) || element.header.startsWith('UDT') ? element.header : 'Formation below the ' + element.header + ' Shoe',
                x: this.plotData.map(r => r[this.columnDefinitions.indexOf(element)]).slice(shoeIndex),
                y: this.selectedDepthDisplay === 'md' ? this.plotData.map(r => r[0]).slice(shoeIndex) : this.plotData.map(r => r[1]).slice(shoeIndex),
                mode: 'lines',
                line: {
                  dash: 'dot',
                  width: 1.25,
                  color: '#9F570D'
                },
                legendgroup: element.header,
                showlegend: false
              });
            }
          } else {
            traceArray.push({
              name: element.header,
              x: this.plotData.map(r => r[this.columnDefinitions.indexOf(element)]),
              y: this.selectedDepthDisplay === 'md' ? this.plotData.map(r => r[0]) : this.plotData.map(r => r[1]),
              mode: 'lines',
              line: {
                dash: 'dot',
                width: 1.25
              },
              legendgroup: element.header
            });
          }
        }
      }
    }));

    if (this.plotName?.startsWith('thermalTemperatureResults') && traceArray.some(trace => trace.name.startsWith('Formation'))) {
      const formationTrace = {
        name: 'Formation',
        x: [0, 0],
        y: [0, 0],
        mode: 'lines',
        line: {
          dash: 'dot',
          width: 1.25,
          color: '#9F570D'
        }
      };
      traceArray.push(formationTrace);
    }

    traceArray.forEach((trace, index) => {
      if (trace.name.startsWith(`UDT`)) {
        traceArray[index].line.color = 'red';
        traceArray[index].line.dash = 'dot';
      }
    });

    this.plot.layout.xaxis['hoverformat'] = this.xAxisTickFormatNumber === '' ? '.2f' : '.' + this.xAxisTickFormatNumber + 'f';
    this.plot.layout.xaxis['tickformat'] = this.xAxisTickFormatNumber === '' ? '.2f' : '.' + this.xAxisTickFormatNumber + 'f';
    this.plot.data = traceArray;
    this.yAxisTitle = this.selectedDepthDisplay === 'md' ? `MD (${this.depthUnit})` : `TVD (${this.depthUnit})`;
    this.missingResults = this.plot.data.length === 0;
  }
}
